import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const usd = (n) => (Math.abs(n || 0) >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`);

/** How the recommended steps and the facilities answer the KPL Strategic Plan 2022–2027. */
export const KplAlignment = ({ show = "all" }) => {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/kpl-alignment").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d) return null;
  const wants = (part) => show === "all" || show === part;

  return (
    <section className="border-t border-zinc-900 bg-black py-16 md:py-20" data-testid="kpl-alignment">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <p className="overline text-ochre">Kenya Premier League Strategic Plan 2022&ndash;2027</p>
        <h2 className="display-2 mt-5 max-w-4xl">{d.heading}</h2>
        <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>

        <div className="mt-10 grid gap-px bg-zinc-800 md:grid-cols-2 xl:grid-cols-4" data-testid="kpl-pillars">
          {d.pillars.map((p) => (
            <div key={p.id} className="bg-[#0b0b0b] p-6" data-testid={`kpl-pillar-${p.id}`}>
              <p className="mono text-[0.58rem] uppercase tracking-[0.2em] text-ochre">{p.n} · {p.title}</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{p.kpl}</p>
              <p className="mt-4 border-l-2 border-maasai pl-4 text-sm leading-relaxed text-white">
                {p.delivered_by}
              </p>
            </div>
          ))}
        </div>

        {wants("merges") ? (
          <div className="mt-14" data-testid="kpl-merges">
            <h3 className="font-display text-lg uppercase tracking-tight text-white md:text-xl">
              Where the plan says run two steps as one
            </h3>
            <div className="mt-6 grid gap-px bg-zinc-800 md:grid-cols-2">
              {d.merges.map((m) => (
                <div key={m.title} className="bg-[#0b0b0b] p-6"
                  data-testid={`kpl-merge-${m.pillar}`}>
                  <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-ochre">{m.pillar_title}</p>
                  <p className="mt-3 font-display text-sm uppercase text-white">{m.title}</p>
                  <p className="mono mt-3 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
                    {m.steps.join(" + ")}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{m.why}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {wants("facilities") ? (
          <div className="mt-14" data-testid="kpl-multi-sport">
            <h3 className="font-display text-lg uppercase tracking-tight text-white md:text-xl">
              The Multi-Sports Division, written into the buildings
            </h3>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.multi_sport_note}</p>
            <div className="mt-6 overflow-x-auto border border-zinc-900">
              <table className="w-full min-w-[860px] text-left" data-testid="kpl-multi-sport-table">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                    {["Facility", "Also carries", "Specification", "Extra spec, per unit", "Extra hire a year", "Pays back in"].map((h) => (
                      <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.multi_sport.map((m) => (
                    <tr key={m.item} className="border-b border-zinc-900"
                      data-testid={`kpl-multi-sport-${m.item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td className="px-4 py-4 text-sm text-white">{m.item}</td>
                      <td className="mono px-4 py-4 text-xs text-zinc-300">{m.sports.join(", ")}</td>
                      <td className="px-4 py-4 text-xs leading-relaxed text-zinc-400">{m.spec}</td>
                      <td className="mono px-4 py-4 text-xs text-zinc-300">{usd(m.extra_spec_usd_per_unit)}</td>
                      <td className="mono px-4 py-4 text-xs text-ochre">
                        {usd(m.annual_hire_usd_per_unit)}
                        <span className="ml-2 text-zinc-500">{m.extra_hire_hours_week}h a week</span>
                      </td>
                      <td className="mono px-4 py-4 text-xs text-zinc-300">
                        {m.payback_years ? `${m.payback_years} years` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#140406]" data-testid="kpl-multi-sport-totals">
                    <td className="px-4 py-4 text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white">
                      Across the whole programme
                    </td>
                    <td className="mono px-4 py-4 text-xs text-zinc-400">All units</td>
                    <td className="px-4 py-4" />
                    <td className="mono px-4 py-4 text-sm text-white">{usd(d.multi_sport_totals.extra_spec_usd)}</td>
                    <td className="mono px-4 py-4 text-sm text-ochre">{usd(d.multi_sport_totals.annual_hire_usd)}</td>
                    <td className="px-4 py-4" />
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="mono mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">
              Across the whole programme: {usd(d.multi_sport_totals.extra_spec_usd)} of extra specification,{" "}
              {usd(d.multi_sport_totals.annual_hire_usd)} of non-football hire income a year
            </p>
          </div>
        ) : null}

        {wants("league") ? (
          <div className="mt-16" data-testid="kpl-governors-league">
            <p className="overline text-ochre">KPL&rsquo;s own county competition</p>
            <h3 className="display-3 mt-4">{d.governors_league.title}</h3>
            <p className="body-copy mt-5 max-w-3xl">{d.governors_league.standfirst}</p>

            <div className="mt-8 grid gap-10 lg:grid-cols-2">
              <div>
                <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">What it is</p>
                <ul className="mt-4 space-y-3">
                  {d.governors_league.what_it_is.map((t) => (
                    <li key={t} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-maasai" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">Why it fits these facilities</p>
                <ul className="mt-4 space-y-3">
                  {d.governors_league.why_it_fits.map((t) => (
                    <li key={t} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-ochre" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 overflow-x-auto border border-zinc-900">
              <table className="w-full min-w-[560px] text-left" data-testid="kpl-league-cost-table">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                    {["Season cost line", "Per fixture", "Per county season"].map((h) => (
                      <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.governors_league.season_cost.map((r) => (
                    <tr key={r.line} className="border-b border-zinc-900">
                      <td className="px-4 py-3.5 text-sm text-white">{r.line}</td>
                      <td className="mono px-4 py-3.5 text-xs text-zinc-300">${r.usd_per_fixture}</td>
                      <td className="mono px-4 py-3.5 text-xs text-zinc-300">${r.usd_per_season.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#140406]">
                    <td className="px-4 py-3.5 text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white">
                      {d.governors_league.fixtures_per_county_season} fixtures a county season
                    </td>
                    <td className="mono px-4 py-3.5 text-sm text-white">${d.governors_league.usd_per_fixture}</td>
                    <td className="mono px-4 py-3.5 text-sm text-ochre">
                      {usd(d.governors_league.usd_per_county_season)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mono mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">
              All 47 counties, one season: {usd(d.governors_league.usd_all_47_season)}
            </p>
            <p className="mt-4 max-w-3xl text-xs leading-relaxed text-zinc-500">{d.governors_league.note}</p>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <Link to="/governors-league" className="btn-ghost" data-testid="kpl-to-governors-league">
                The Governors&rsquo; League in full
              </Link>
              <a href={d.governors_league.source.url} target="_blank" rel="noopener noreferrer"
                className="mono inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
                data-testid="kpl-league-source">
                {d.governors_league.source.label} <ExternalLink size={12} />
              </a>
            </div>
          </div>
        ) : null}

        {wants("roadmap") ? (
          <div className="mt-16" data-testid="kpl-roadmap">
            <p className="overline text-ochre">The league&rsquo;s own roadmap</p>
            <div className="mt-6 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3">
              {d.roadmap.map(([year, note]) => (
                <div key={year} className="bg-[#0b0b0b] p-5">
                  <p className="mono text-[0.58rem] uppercase tracking-[0.2em] text-ochre">{year}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{note}</p>
                </div>
              ))}
            </div>
            <div className="mono mt-6 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em]">
              <Link to="/plans" className="text-ochre hover:text-white" data-testid="kpl-to-plans">The recommended steps</Link>
              <Link to="/facilities" className="text-ochre hover:text-white" data-testid="kpl-to-facilities">The facility library</Link>
              <Link to="/counties/rollup" className="text-ochre hover:text-white" data-testid="kpl-to-rollup">The national rollup</Link>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};
